/**
 * Filter Presets API Client
 * Handles API calls for timesheet report filter presets
 */

import { apiClient, ApiError } from '@/services/apiClient';
import type { TimesheetReportFilters } from '@/types/timesheetReport';

export interface FilterPresetDto {
  id: string;
  name: string;
  description?: string;
  filters: string;
  isDefault: boolean;
  isShared: boolean;
  createdBy: string;
  createdAt: string;
  usageCount: number;
  type: 'personal' | 'team' | 'system';
}

export interface CreateFilterPresetDto {
  name: string;
  description?: string;
  filters: string;
  isDefault: boolean;
  isShared: boolean;
  type: 'personal' | 'team' | 'system';
}

export interface UpdateFilterPresetDto {
  name: string;
  description?: string;
  filters: string;
  isDefault: boolean;
  isShared: boolean;
}

export interface FilterPreset {
  id: string;
  name: string;
  description?: string;
  filters: TimesheetReportFilters;
  isDefault: boolean;
  isShared: boolean;
  createdBy: string;
  createdAt: Date;
  usageCount: number;
  type: 'personal' | 'team' | 'system';
}

function parseFilters(filtersJson: string): TimesheetReportFilters {
  try {
    const parsed = JSON.parse(filtersJson);
    return {
      ...parsed,
      dateFrom: parsed.dateFrom ? new Date(parsed.dateFrom) : undefined,
      dateTo: parsed.dateTo ? new Date(parsed.dateTo) : undefined,
    };
  } catch {
    return {};
  }
}

function serializeFilters(filters: TimesheetReportFilters): string {
  return JSON.stringify(filters);
}

function fromDto(dto: FilterPresetDto): FilterPreset {
  return {
    id: dto.id,
    name: dto.name,
    description: dto.description,
    filters: parseFilters(dto.filters),
    isDefault: dto.isDefault,
    isShared: dto.isShared,
    createdBy: dto.createdBy,
    createdAt: new Date(dto.createdAt),
    usageCount: dto.usageCount,
    type: dto.type,
  };
}

/**
 * Get all filter presets for the current user
 * Includes personal presets, shared presets, and system presets
 */
export async function getFilterPresets(): Promise<FilterPreset[]> {
  const dtos = await apiClient.get<FilterPresetDto[]>('/filterpresets');
  return dtos.map(fromDto);
}

/**
 * Get a single filter preset by ID
 */
export async function getFilterPreset(id: string): Promise<FilterPreset> {
  const dto = await apiClient.get<FilterPresetDto>(`/filterpresets/${id}`);
  return fromDto(dto);
}

/**
 * Create a new filter preset
 */
export async function createFilterPreset(
  preset: Omit<FilterPreset, 'id' | 'createdAt' | 'usageCount' | 'createdBy'>
): Promise<FilterPreset> {
  const createDto: CreateFilterPresetDto = {
    name: preset.name,
    description: preset.description,
    filters: serializeFilters(preset.filters),
    isDefault: preset.isDefault,
    isShared: preset.isShared,
    type: preset.type,
  };
  
  const dto = await apiClient.post<FilterPresetDto, CreateFilterPresetDto>('/filterpresets', createDto);
  return fromDto(dto);
}

/**
 * Update an existing filter preset
 */
export async function updateFilterPreset(
  id: string,
  preset: Omit<FilterPreset, 'id' | 'createdAt' | 'usageCount' | 'createdBy' | 'type'>
): Promise<FilterPreset> {
  const updateDto: UpdateFilterPresetDto = {
    name: preset.name,
    description: preset.description,
    filters: serializeFilters(preset.filters),
    isDefault: preset.isDefault,
    isShared: preset.isShared,
  };
  
  const dto = await apiClient.put<FilterPresetDto, UpdateFilterPresetDto>(`/filterpresets/${id}`, updateDto);
  return fromDto(dto);
}

/**
 * Delete a filter preset
 */
export async function deleteFilterPreset(id: string): Promise<void> {
  await apiClient.delete(`/filterpresets/${id}`);
}

/**
 * Set a filter preset as the default
 */
export async function setDefaultPreset(id: string): Promise<void> {
  await apiClient.put(`/filterpresets/${id}/set-default`, {});
}

/**
 * Increment the usage count for a preset (called when loading a preset)
 */
export async function incrementPresetUsage(id: string): Promise<void> {
  await apiClient.post(`/filterpresets/${id}/use`, {});
}

export { ApiError };
